import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTimelinePage = () => {
  const [timeline, setTimeline] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchTimeline();
  }, []);

  const fetchTimeline = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/timeline`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const handleSubmit = async () => {
    const auth = localStorage.getItem('admin_auth');
    // Send only the fields the backend model expects so stale _fr/_nl/_en/_es
    // values don't overwrite fresh translations
    const payload = {
      year: parseInt(formData.year, 10),
      title: formData.title || '',
      description: formData.description || '',
    };
    try {
      if (editingEntry) {
        await axios.put(`${BACKEND_URL}/api/admin/timeline/${editingEntry._id}`, payload, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Entrée mise à jour!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/timeline`, payload, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Entrée ajoutée!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTimeline();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/timeline/${entryId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Entrée supprimée!');
      fetchTimeline();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData(entry);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      year: new Date().getFullYear(),
      title: '',
      description: ''
    });
  };

  return (
    <div data-testid="admin-timeline-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Timeline</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez l'historique d'Invasion Latina</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0C] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-[#F5F0E8] font-display">
                {editingEntry ? 'Modifier l\'entrée' : 'Nouvelle entrée'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Année</label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  disabled={!!editingEntry}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Événement important"
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                {editingEntry ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Timeline List */}
      <div className="space-y-3">
        {timeline.map((entry, index) => (
          <motion.div
            key={entry._id || entry.year}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-4"
          >
            <div className="w-16 h-16 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
              <span className="font-display text-xl text-[#C9A84C]">{entry.year}</span>
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg text-[#F5F0E8] mb-1">{entry.title}</h3>
              <p className="text-sm text-[#B9B2A8]">{entry.description}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={() => openEditDialog(entry)} className="text-[#B9B2A8] hover:text-[#F5F0E8]">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(entry._id)} className="text-[#B9B2A8] hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AdminTimelinePage;
